import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { IconLogo } from '../components/Icons.js';
import { Spinner } from '../components/Spinner.js';

/**
 * Standalone challenge page shown after Discord OAuth when the user has 2FA
 * enabled. Verifies a 6-digit TOTP (or one of their recovery codes) against
 * the short-lived `twofa_pending` cookie set by the OAuth callback.
 */
export default function TwoFactorChallenge() {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [phase, setPhase] = useState<'loading' | 'ready' | 'expired'>('loading');

  useEffect(() => {
    api.get<{ pending: boolean; username?: string }>('/api/auth/2fa/pending')
      .then((r) => {
        if (r.pending) {
          setUsername(r.username ?? null);
          setPhase('ready');
        } else {
          setPhase('expired');
        }
      })
      .catch(() => setPhase('expired'));
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/auth/2fa/challenge', { code: code.trim() });
      window.location.href = '/';
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  if (phase === 'loading') {
    return (
      <div className="h-full flex items-center justify-center">
        <Spinner size={28} className="text-accent" />
      </div>
    );
  }

  if (phase === 'expired') {
    return (
      <div className="h-full flex items-center justify-center bg-bg p-4">
        <div className="card p-8 max-w-md w-full text-center space-y-4">
          <h1 className="text-lg font-semibold">Session expired</h1>
          <p className="text-sm text-fg-muted">Your 2FA challenge timed out. Please sign in again.</p>
          <a href="/api/auth/login" className="btn-primary inline-flex">Back to sign in</a>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex items-center justify-center bg-bg p-4">
      <div className="w-full max-w-md">
        <form onSubmit={submit} className="card p-8 sm:p-10 flex flex-col items-center gap-5 shadow-pop">
          <div className="flex items-center gap-3">
            <IconLogo size={40} />
            <div>
              <h1 className="text-lg font-semibold tracking-tight">Two-step verification</h1>
              {username && <p className="text-fg-muted text-xs">Signed in as {username}</p>}
            </div>
          </div>

          <p className="text-center text-sm text-fg-muted leading-relaxed max-w-xs">
            Enter the 6-digit code from your authenticator app, or one of your recovery codes.
          </p>

          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            placeholder="123 456"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="input !h-12 text-center !text-xl tracking-[0.4em] font-mono"
            maxLength={11}
          />

          {error && (
            <div className="w-full text-sm text-status-error px-3 py-2 rounded-md bg-status-error/10 border border-status-error/30">
              {error}
            </div>
          )}

          <button type="submit" className="btn-primary w-full !h-11" disabled={busy || code.trim().length < 6}>
            {busy ? 'Verifying…' : 'Verify'}
          </button>

          <a href="/api/auth/logout" className="text-xs text-fg-dim hover:text-fg">Cancel and sign out</a>
        </form>
      </div>
    </div>
  );
}
